import React from 'react';
import { TextField, FormHelperText, Box } from '@mui/material';
import BaseInputErrors from './BaseInputErrors';
import InputTooltip from './InputTooltip';
import { BaseInputLayoutProps, getCommonInputProps } from './BaseInputLayout';
import { CommonInputLayoutProps, FormFieldsData } from 'framework/form';
import floatingInputSx from './floatingInputSx';

interface FloatingLabelInputLayoutProps extends BaseInputLayoutProps {
  type?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const FloatingLabelInputLayout = (props: FloatingLabelInputLayoutProps) => {
  const {
    label,
    source,
    errorMessages = [],
    isVisible = true,
    isRequired = false,
    helpText = null,
    tooltip = null,
    className = '',
    sx,
    type = 'text',
    multiline = false,
    rows,
    maxRows,
    labelHint,
    placeholder,
    errorMessageMapper,
  } = props;

  if (!isVisible) {
    return null;
  }
  const commonInputProps = getCommonInputProps(props as CommonInputLayoutProps, false);
  const hasError = !!errorMessages?.length;
  const requiredValue = typeof isRequired === 'function' ? isRequired({} as FormFieldsData) : !!isRequired;
  const labelText = typeof label === 'function' ? label() : label || placeholder;

  const renderLabel = () => {
    if (!labelText) {
      return null;
    }

    if (!tooltip && requiredValue) {
      return (
        <>
          {labelText}
          <span> *</span>
        </>
      );
    }

    if (!tooltip) {
      return labelText;
    }

    if (requiredValue) {
      return (
        <>
          {labelText}
          <span> *</span>
          <InputTooltip message={tooltip} source={source} size="small" />
        </>
      );
    }

    return (
      <>
        {labelText}
        <InputTooltip message={tooltip} source={source} size="small" />
      </>
    );
  };

  return (
    <Box
      className={`floating-label-input ${className}`}
      sx={[
        floatingInputSx,
        {
          transition: 'padding 0.3s ease',
          paddingBottom: hasError ? 1.5 : 0,
          '& .MuiInputLabel-root': {
            transform: 'translate(16px, 9px)',
            transformOrigin: 'top left',
            transition: 'all 0.2s ease-out',
          },
          '& .MuiInputLabel-shrink': { transform: 'translate(14px, -7px) scale(0.75)' },
          ...(tooltip
            ? {
                cursor: 'help',
                '& .MuiOutlinedInput-root, & .react-select__control': { cursor: 'help' },
              }
            : {}),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <TextField
        id={source}
        name={commonInputProps.name}
        label={renderLabel()}
        type={type}
        value={commonInputProps.value ?? ''}
        onChange={commonInputProps.onChange}
        onBlur={commonInputProps.onBlur}
        onKeyDown={commonInputProps.onKeyDown}
        onFocus={commonInputProps.onFocus}
        disabled={commonInputProps.disabled}
        error={hasError}
        placeholder=""
        multiline={multiline}
        rows={rows}
        maxRows={maxRows}
        variant="outlined"
        fullWidth
        className={hasError ? 'has-error' : undefined}
        slotProps={{
          input: {
            sx: {
              '& textarea': {
                resize: 'vertical',
              },
            },
          },
          ...commonInputProps.slotProps,
        }}
      />
      {labelHint && (
        <FormHelperText
          sx={{
            position: 'absolute',
            top: '-8px',
            right: '12px',
            fontSize: '0.65rem',
            color: 'text.secondary',
            backgroundColor: 'background.paper',
            padding: '0 4px',
            zIndex: 1,
            transition: 'all 0.2s ease-out',
            margin: 0,
          }}
        >
          {labelHint}
        </FormHelperText>
      )}
      {(hasError || helpText) && (
        <FormHelperText
          error={hasError}
          sx={{
            position: 'relative',
            '&.Mui-error': {
              zIndex: 1,
              display: 'inline-block',
              fontSize: '0.7rem',
              lineHeight: 1.2,
              marginBottom: 0,
            },
          }}
        >
          {hasError ? (
            <BaseInputErrors errorMessages={errorMessages} errorMessageMapper={errorMessageMapper} />
          ) : (
            helpText
          )}
        </FormHelperText>
      )}
    </Box>
  );
};

export default FloatingLabelInputLayout;
